/**
 * usePaymentCheckout Hook
 *
 * Handles Stripe Checkout session creation via create-checkout Edge Function.
 * For paid waitlist tiers (Priority Access $1, Founding Member $5).
 */

import { useCallback, useState } from "react";

import { trackConversion, trackUpsellFunnel } from "../utils/analytics";
import { publicAnonKey, supabaseUrl } from "../utils/supabase/info";

export type PaymentTier = "priority" | "founding";

interface CheckoutState {
  isLoading: boolean;
  error: string | null;
}

interface UsePaymentCheckoutReturn extends CheckoutState {
  initiateCheckout: (email: string, tier: PaymentTier) => Promise<void>;
  clearError: () => void;
}

export function usePaymentCheckout(): UsePaymentCheckoutReturn {
  const [state, setState] = useState<CheckoutState>({
    isLoading: false,
    error: null,
  });

  const initiateCheckout = useCallback(
    async (email: string, tier: PaymentTier) => {
      setState({ isLoading: true, error: null });

      try {
        // Track checkout initiation
        trackUpsellFunnel("payment_initiated", {
          tier,
        });

        const response = await fetch(
          `${supabaseUrl}/functions/v1/create-checkout`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${publicAnonKey}`,
            },
            body: JSON.stringify({ email, tier }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || `Checkout failed: ${response.status}`
          );
        }

        const data = await response.json();

        if (!data.url) {
          throw new Error("No checkout URL returned");
        }

        // Track successful checkout URL generation - use generic event since checkout_redirect isn't in the union
        trackConversion("demo_start", "checkout", undefined, {
          tier,
          session_id: data.sessionId,
        });

        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } catch (error) {
        console.error("Checkout error:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to initiate payment. Please try again.";

        setState({ isLoading: false, error: errorMessage });
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    initiateCheckout,
    clearError,
  };
}
